/**
 * apiClient - Centralized fetch wrapper with automatic token refresh
 *
 * - Injects Authorization header automatically on every request
 * - On 401: attempts to refresh the access token once
 *   - Success → retries the original request with the new token
 *   - Failure → clears session and redirects to /login
 */

import {
  getToken,
  getRefreshToken,
  refreshAccessToken,
  logout,
} from "./authService";

const ACCESS_TOKEN_KEY = "access_token";

/** Whether a refresh is already in progress (prevents parallel refresh storms) */
let isRefreshing = false;

/** Queue of callbacks waiting for the new token */
let refreshSubscribers: Array<(token: string | null) => void> = [];

function onTokenRefreshed(token: string | null) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

function subscribeToRefresh(cb: (token: string | null) => void) {
  refreshSubscribers.push(cb);
}

function buildHeaders(extraHeaders?: HeadersInit): Record<string, string> {
  const token = getToken();
  const base: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    base["Authorization"] = `Bearer ${token}`;
  }
  // Merge any extra headers passed by the caller
  if (extraHeaders) {
    const extra =
      extraHeaders instanceof Headers
        ? Object.fromEntries(extraHeaders.entries())
        : (extraHeaders as Record<string, string>);
    Object.assign(base, extra);
  }
  return base;
}

function redirectToLogin() {
  // Clear all auth data then hard-navigate to login
  logout().catch(() => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("auth_user");
    localStorage.removeItem("auth_company");
    localStorage.removeItem("auth_branches");
  });
  window.location.href = "/login";
}

/**
 * Drop-in replacement for fetch() that handles token refresh automatically.
 *
 * Usage is identical to native fetch:
 *   const res = await apiFetch("/api/v1/users/");
 *   const res = await apiFetch("/api/v1/users/", { method: "POST", body: JSON.stringify(data) });
 */
export async function apiFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  // First attempt ─────────────────────────────────────────────────────────────
  const firstResponse = await fetch(url, {
    ...options,
    headers: buildHeaders(options.headers),
  });

  if (firstResponse.status !== 401) {
    return firstResponse;
  }

  // Got 401 ─── try to refresh ─────────────────────────────────────────────────
  if (!getRefreshToken()) {
    // No refresh token at all → go to login right away
    redirectToLogin();
    return firstResponse;
  }

  if (isRefreshing) {
    // Another request is already refreshing; wait for it
    return new Promise<Response>((resolve) => {
      subscribeToRefresh(async (newToken) => {
        if (!newToken) {
          redirectToLogin();
          resolve(firstResponse);
          return;
        }
        const retryResponse = await fetch(url, {
          ...options,
          headers: {
            ...buildHeaders(options.headers),
            Authorization: `Bearer ${newToken}`,
          },
        });
        resolve(retryResponse);
      });
    });
  }

  isRefreshing = true;

  const refreshed = await refreshAccessToken();

  isRefreshing = false;

  if (!refreshed) {
    onTokenRefreshed(null);
    redirectToLogin();
    return firstResponse;
  }

  const newToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  onTokenRefreshed(newToken);

  // Retry original request with the new token ─────────────────────────────────
  return fetch(url, {
    ...options,
    headers: {
      ...buildHeaders(options.headers),
      Authorization: `Bearer ${newToken}`,
    },
  });
}
